import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { faFileLines, faUpload } from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ExamGridModel, FileModel, PatientModel, TagModel } from 'models';

import { useApiClient } from 'features/api';
import { useSignalRRefresh } from 'features/auth/hooks/useSignalRRefresh';
import { ImgProxy } from 'features/file/fragments/ImgProxy';
import { PatientFilesUploadModalProps, PatientMediaUploadModal } from 'features/patient/fragments/PatientMediaUploadModal';
import { useTagLookups } from 'features/tags/hooks/useTagLookups';
import { UploadPipelineProvider } from 'features/upload-pipeline/fragments/UploadPipelineProvider';

import { Button, ButtonLooks, ButtonVariants, ComponentSizes, Icon, ProgressSpinnerOverlay, Window } from '../../../core/ui';
import { useFileViewerContext } from '../contexts/FileViewerContext';
import { sortFilesByDate as utilSortFilesByDate } from '../services/file-category-helper';

// -----------------------------------
// Styled components (original + small contrast tweaks)
// -----------------------------------
const PageLayout = styled.div`
  display: flex;
  height: 100%;
  max-height: calc(100vh - 160px);
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    max-height: none;
  }
`;

const MainContentArea = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Sidebar = styled.div`
  width: 300px;
  background-color: #f9f9f9;
  padding: 1rem;
  border-left: 1px solid #ddd;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const UploadButtonContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FileCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #fafafa;
  border-bottom: 1px solid #ddd;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const StatusTag = styled.span<{ isComplete: boolean }>`
  display: inline-block;
  font-weight: 500;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${({ isComplete }) => (isComplete ? '#4caf50' : '#ffc107')};
  color: ${({ isComplete }) => (isComplete ? '#ffffff' : '#333')};
`;

const CardContent = styled.div`
  padding: 1rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const FileThumbnail = styled.div`
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  margin-bottom: 15px;
  overflow: hidden;
`;

const FileInfo = styled.div`
  margin-top: 15px;
  flex-grow: 1;
`;

const FileText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const DashboardContainer = styled.div`
  margin-bottom: 30px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DashboardTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const DashboardStatus = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 15px;
`;

const ProgressCircle = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const ProgressInnerCircle = styled.div`
  position: absolute;
  width: 120px;
  height: 120px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 90px;
    height: 90px;
  }
`;

const ProgressValue = styled.div`
  position: relative;
  font-size: 2rem;
  font-weight: bold;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 14px;
  margin-bottom: 1rem;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }

  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const TagGroupProgress = styled.div`
  margin-top: 30px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const TagGroupProgressItem = styled.div`
  margin-bottom: 15px;
`;

const TagGroupName = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  font-size: 1rem;
  font-weight: 600;

  &:before {
    content: '';
    display: inline-block;
    width: 15px;
    height: 15px;
    margin-right: 10px;
    border-radius: 3px;
    background-color: ${({ color }) => color ?? '#6c757d'};
    flex-shrink: 0;
  }
`;

const ProgressBar = styled.div<{ percent?: string; color?: string }>`
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ percent }) => percent ?? '0%'};
    background-color: ${({ color }) => color ?? '#ffd700'};
    border-radius: 4px;
  }
`;

const MissingTagsList = styled.ul`
  list-style: none;
  padding-left: 25px;
  margin-top: 5px;
  margin-bottom: 10px;
  font-size: 0.85rem;
  color: #dc3545;
`;

const MissingTagItem = styled.li`
  &:before {
    content: '• ';
    color: #dc3545;
    margin-right: 4px;
  }
`;

const AIRecommendations = styled.div`
  margin-top: 40px;
`;

const RecommendationItem = styled.div`
  margin-bottom: 15px;
  font-size: 14px;
  line-height: 1.5;
`;

const ContactInfo = styled.div`
  margin-top: 10px;
  font-size: 14px;
`;

const FileListItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
  padding: 5px 0;
`;

const StyledCategoryFilesContainer = styled.div`
  padding: 1rem;
  max-height: 60vh;
  overflow-y: auto;
`;

// -----------------------------------
// Interfaces and Constants
// -----------------------------------
interface PatientFilesDashboardProps {
  patientId: number;
  className?: string;
  gridData?: ExamGridModel[];
  onViewFile: (fileToView: FileModel, contextFiles: FileModel[]) => void;
  targetParentTagIds?: number[];
  showPreviewSidebar?: boolean; // Whether to show preview sidebar instead of full file viewer
  onFileSelect?: (file: FileModel | null) => void; // Handler for preview selection
  // Optional tag data props - if not provided, will use useTagLookups
  allTagsFlat?: TagModel[];
  tagIdToTagMap?: Map<number, TagModel>;
  areTagsLoading?: boolean;
}

interface TagGroup {
  parentTagId: number;
  parentTagName: string;
  childTagIds: number[];
  allAssociatedTagIds: number[];
  files: FileModel[];
  hasParentFile: boolean;
  missingChildTagNames: string[];
  isComplete: boolean;
  color: string;
}

const TAG_COLORS: Record<number, string> = {
  1: '#007bff',
  10: '#28a745',
  25: '#ffc107',
  2: '#6f42c1',
  6: '#fd7e14',
  12: '#17a2b8',
};
const DEFAULT_TAG_COLOR = '#6c757d';
const DEFAULT_TARGET_TAG_IDS = [2, 6, 10, 12];

// -----------------------------------
// UI Helper Components
// -----------------------------------
const FileThumbnailDisplay = ({ file }: { file: FileModel | null }) => {
  if (!file) {
    return <div style={{ fontSize: '48px', color: '#ccc' }}>📄</div>;
  }

  return file.thumbnailUrl ? (
    <ImgProxy
      fileId={file.id}
      thumbnailUrl={file.thumbnailUrl ?? file.thumbnailLocation ?? ''}
      dateModified={file.modified}
      isReadOnly
      height={120}
      width="100%"
    />
  ) : (
    <div style={{ fontSize: '48px' }}>📄</div>
  );
};

const FileInfoDisplay = ({ file, displayName }: { file: FileModel | null; displayName: string }) => {
  if (!file) {
    return (
      <>
        <FileText>No {displayName.toLowerCase()} files uploaded yet.</FileText>
      </>
    );
  }

  const date = file.created ? dayjs(file.created).format('MM/DD/YYYY') : 'No date';

  return (
    <>
      <FileText>{file.fileName}</FileText>
      <FileText>
        <small>Date: {date}</small>
      </FileText>
    </>
  );
};

const TagGroupCard = ({
  tagGroup,
  onCardClick,
  onViewAll,
  onUploadClick,
}: {
  tagGroup: TagGroup;
  onCardClick: (file: FileModel, tagGroup: TagGroup) => void;
  onViewAll: (tagGroup: TagGroup) => void;
  onUploadClick: (tagGroupId: number) => void;
}) => {
  const sortedFiles = utilSortFilesByDate(tagGroup.files);
  const mostRecentFile = sortedFiles[0] || null;
  const hasFiles = sortedFiles.length > 0;

  const handleUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUploadClick(tagGroup.parentTagId);
  };

  return (
    <FileCard key={tagGroup.parentTagId} onClick={() => (hasFiles ? onCardClick(mostRecentFile, tagGroup) : undefined)}>
      <CardHeader>
        <CardTitle>{tagGroup.parentTagName}</CardTitle>
        <StatusTag isComplete={tagGroup.isComplete}>{tagGroup.isComplete ? 'COMPLETE' : 'INCOMPLETE'}</StatusTag>
      </CardHeader>
      <CardContent>
        <FileThumbnail>
          <FileThumbnailDisplay file={mostRecentFile} />
        </FileThumbnail>

        {mostRecentFile && <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#4caf50' }}>Latest File</div>}

        <FileInfo>
          <FileInfoDisplay file={mostRecentFile} displayName={tagGroup.parentTagName} />
          <div style={{ marginTop: 'auto', paddingTop: '10px', fontSize: '0.85rem', color: '#666' }}>
            {tagGroup.files.length} file(s) found.
            {!tagGroup.isComplete && tagGroup.missingChildTagNames.length > 0 && (
              <div style={{ color: '#dc3545', marginTop: '5px' }}>Missing: {tagGroup.missingChildTagNames.join(', ')}</div>
            )}
            {!tagGroup.isComplete && !tagGroup.hasParentFile && (
              <div style={{ color: '#dc3545', marginTop: '5px' }}>Missing file with primary tag: {tagGroup.parentTagName}</div>
            )}
          </div>
        </FileInfo>
        <Button
          look={ButtonLooks.STANDARD}
          variant={ButtonVariants.SECONDARY}
          onClick={handleUpload}
          style={{ marginTop: '10px', width: '100%' }}
          size={ComponentSizes.SMALL}
          title={`Upload file for ${tagGroup.parentTagName}`}
        >
          <Icon icon={faUpload} style={{ marginRight: '5px' }} /> Upload to Group
        </Button>
      </CardContent>
    </FileCard>
  );
};

const Dashboard = ({ tagGroups, onUpload }: { tagGroups: TagGroup[]; onUpload: () => void }) => {
  const totalGroups = tagGroups.length;
  const completedGroups = tagGroups.filter((group) => group.isComplete).length;
  const progressPercent = totalGroups > 0 ? Math.round((completedGroups / totalGroups) * 100) : 0;

  const getCircleStyle = useCallback(() => {
    const angle = (progressPercent / 100) * 360;
    return {
      background: `conic-gradient(#4CAF50 ${angle}deg, #e0e0e0 ${angle}deg)`,
    };
  }, [progressPercent]);

  return (
    <DashboardContainer>
      <DashboardTitle>Required Tag Groups Status</DashboardTitle>
      <DashboardStatus>
        {completedGroups} of {totalGroups} required groups complete
      </DashboardStatus>

      <ProgressCircle style={getCircleStyle()}>
        <ProgressInnerCircle>
          <ProgressValue>{progressPercent}%</ProgressValue>
        </ProgressInnerCircle>
      </ProgressCircle>

      <ProgressText>
        {progressPercent === 100
          ? 'All required groups complete!'
          : progressPercent >= 50
            ? 'Good progress, some groups incomplete.'
            : 'More documents needed for required groups.'}
      </ProgressText>
      <Button look={ButtonLooks.STANDARD} variant={ButtonVariants.PRIMARY} onClick={onUpload} style={{ width: '100%' }}>
        Upload Files
      </Button>
    </DashboardContainer>
  );
};

const TagGroupProgressDisplay = React.memo(({ tagGroups }: { tagGroups: TagGroup[] }) => (
  <TagGroupProgress>
    {tagGroups.map((group) => (
      <TagGroupProgressItem key={group.parentTagId}>
        <TagGroupName color={group.color}>{group.parentTagName}</TagGroupName>
        <ProgressBar percent={group.isComplete ? '100' : '0'} color={group.isComplete ? '#4caf50' : '#e0e0e0'} />
        {!group.isComplete && (
          <MissingTagsList>
            {!group.hasParentFile && <MissingTagItem>Missing primary tag: {group.parentTagName}</MissingTagItem>}
            {group.missingChildTagNames.map((name) => (
              <MissingTagItem key={name}>Missing child tag: {name}</MissingTagItem>
            ))}
          </MissingTagsList>
        )}
      </TagGroupProgressItem>
    ))}
    {tagGroups.length === 0 && <p style={{ fontSize: '0.9rem', color: '#6c757d' }}>No target tag groups defined or found.</p>}
  </TagGroupProgress>
));
TagGroupProgressDisplay.displayName = 'TagGroupProgressDisplay';

// -----------------------------------
// Main PatientFilesDashboard component
// -----------------------------------
const PatientFilesDashboardComponent: React.FC<PatientFilesDashboardProps> = ({
  patientId,
  className: _className,
  gridData = [],
  onViewFile,
  targetParentTagIds: targetParentTagIdsProp,
  showPreviewSidebar = false,
  onFileSelect,
  allTagsFlat: allTagsFlatProp,
  tagIdToTagMap: tagIdToTagMapProp,
  areTagsLoading: areTagsLoadingProp,
}) => {
  const apiClient = useApiClient();
  const fileViewerContext = useFileViewerContext();
  const [files, setFiles] = useState<FileModel[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewAllTagGroup, setViewAllTagGroup] = useState<TagGroup | null>(null);
  const [targetTagForUpload, setTargetTagForUpload] = useState<TagModel | null>(null);

  // Use provided tag data if available, otherwise fetch using useTagLookups
  const { allFlatTags: fetchedAllFlatTags, tagIdToTagMap: fetchedTagIdToTagMap, isLoadingTags: fetchedIsLoadingTags } = useTagLookups();

  // Check if provided props have meaningful data, otherwise fallback to useTagLookups
  const allFlatTags = allTagsFlatProp && allTagsFlatProp.length > 0 ? allTagsFlatProp : fetchedAllFlatTags;
  const tagIdToTagMap = tagIdToTagMapProp && tagIdToTagMapProp.size > 0 ? tagIdToTagMapProp : fetchedTagIdToTagMap;
  const isLoadingTags = areTagsLoadingProp ?? fetchedIsLoadingTags;

  const targetParentTagIds = targetParentTagIdsProp && targetParentTagIdsProp.length > 0 ? targetParentTagIdsProp : DEFAULT_TARGET_TAG_IDS;

  // --- Helper Functions (Defined inside component scope) ---

  const getAllDescendantTagIds = useCallback((tagId: number, flatTagMap: Map<number, TagModel>): number[] => {
    const result: Set<number> = new Set<number>();
    const queue: number[] = [tagId];
    const visited: Set<number> = new Set<number>();

    while (queue.length > 0) {
      const currentId = queue.shift();
      if (!currentId || visited.has(currentId)) continue;
      visited.add(currentId);

      const tag = flatTagMap.get(currentId);
      if (tag) {
        result.add(tag.id);
        const children = Array.from(flatTagMap.values()).filter((t) => t.parentTagId === currentId);
        children.forEach((child) => {
          if (!visited.has(child.id)) {
            queue.push(child.id);
          }
        });
      }
    }
    return Array.from(result);
  }, []); // Empty dependency array as it doesn't depend on props/state

  const getDirectChildrenIds = useCallback((tagId: number, flatTagMap: Map<number, TagModel>): number[] => {
    return Array.from(flatTagMap.values())
      .filter((tag) => tag.parentTagId === tagId)
      .map((tag) => tag.id);
  }, []); // Empty dependency array

  const fileHasTag = useCallback((file: FileModel, tagId: number): boolean => {
    return file.tags?.some((tag) => tag.id === tagId) ?? false;
  }, []); // Empty dependency array

  const fileHasAnyTag = useCallback((file: FileModel, tagIds: number[]): boolean => {
    return file.tags?.some((tag) => tagIds.includes(tag.id)) ?? false;
  }, []); // Empty dependency array

  const fetchFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    try {
      const patientFiles = await apiClient.filesClient.getFilesByPatientId(patientId);
      const filesWithTags = patientFiles.map((f) => ({ ...f, tags: f.tags || [] }));
      setFiles(filesWithTags);
    } catch (_error) {
      console.error('Failed to fetch files');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [apiClient, patientId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Use SignalR for live refresh
  useSignalRRefresh({
    patientId,
    onPatientFileUploaded: useCallback(async () => {
      console.log('SignalR: File uploaded, refreshing PatientFilesDashboard');
      await fetchFiles();
    }, [fetchFiles]),
    onWorklistUpdated: useCallback(async () => {
      console.log('SignalR: Worklist updated, refreshing PatientFilesDashboard');
      await fetchFiles();
    }, [fetchFiles]),
  });

  const tagGroups = useMemo((): TagGroup[] => {
    if (isLoadingTags || !allFlatTags || allFlatTags.length === 0 || !tagIdToTagMap || !targetParentTagIds || targetParentTagIds.length === 0) {
      return [];
    }

    // Now uses the helper functions defined within the component scope
    return targetParentTagIds
      .map((parentId) => {
        const parentTag = tagIdToTagMap.get(parentId);
        if (!parentTag) {
          console.warn(`Target parent tag with ID ${parentId} not found.`);
          return null;
        }

        const directChildIds = getDirectChildrenIds(parentId, tagIdToTagMap);
        const allAssociatedIds = getAllDescendantTagIds(parentId, tagIdToTagMap);
        const groupFiles = files.filter((file) => fileHasAnyTag(file, allAssociatedIds));
        const hasParentFile = groupFiles.some((file) => fileHasTag(file, parentId));

        const missingChildTagNames: string[] = [];
        directChildIds.forEach((childId: number) => {
          const childHasFile = groupFiles.some((file) => fileHasTag(file, childId));
          if (!childHasFile) {
            const childTag = tagIdToTagMap.get(childId);
            if (childTag) {
              missingChildTagNames.push(childTag.name);
            }
          }
        });

        const isComplete = hasParentFile && missingChildTagNames.length === 0;

        return {
          parentTagId: parentId,
          parentTagName: parentTag.name,
          childTagIds: directChildIds,
          allAssociatedTagIds: allAssociatedIds,
          files: groupFiles,
          hasParentFile,
          missingChildTagNames,
          isComplete,
          color: TAG_COLORS[parentId] ?? DEFAULT_TAG_COLOR,
        };
      })
      .filter((group): group is TagGroup => group !== null);
  }, [files, allFlatTags, tagIdToTagMap, targetParentTagIds, isLoadingTags, getAllDescendantTagIds, getDirectChildrenIds, fileHasAnyTag, fileHasTag]); // Add helpers to dependency array

  const handleCardClick = (file: FileModel, tagGroup: TagGroup) => {
    // Set the current path to indicate we're coming from the Cases view
    fileViewerContext.setCurrentPath(`cases/${tagGroup.parentTagName}`);
    const sortedGroupFiles = utilSortFilesByDate(tagGroup.files);
    onViewFile(file, sortedGroupFiles);
  };

  const handleUploadForTagGroup = useCallback(
    (parentTagId: number) => {
      const targetTag = tagIdToTagMap?.get(parentTagId) ?? null;
      setTargetTagForUpload(targetTag);
      console.log('Upload triggered for tag group:', parentTagId, 'Tag:', targetTag?.name);
      setIsUploadModalOpen(true);
    },
    [tagIdToTagMap],
  );

  const handleGlobalUpload = useCallback(() => {
    setTargetTagForUpload(null);
    setIsUploadModalOpen(true);
  }, []);

  const handleViewAll = (tagGroup: TagGroup) => {
    setViewAllTagGroup(tagGroup);
  };

  const handleViewFileClickFromModal = (file: FileModel, tagGroup: TagGroup | null) => {
    if (!tagGroup) return;
    if (showPreviewSidebar && onFileSelect) {
      // Show preview sidebar instead of full file viewer
      onFileSelect(file);
      setViewAllTagGroup(null);
    } else {
      // Set the current path to indicate we're coming from the Cases view
      fileViewerContext.setCurrentPath(`cases/${tagGroup.parentTagName}`);
      const context = utilSortFilesByDate(tagGroup.files);
      onViewFile(file, context);
      setViewAllTagGroup(null);
    }
  };

  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
    setTargetTagForUpload(null);
  }, []);

  const patientModel: PatientModel = { id: patientId /* other required fields */ } as PatientModel;

  const isLoading = isLoadingFiles || isLoadingTags;

  return (
    <>
      {isLoading && <ProgressSpinnerOverlay show={true} />}
      {isUploadModalOpen && (
        <PatientMediaUploadModal
          patient={patientModel}
          onClose={handleCloseUploadModal}
          onSuccess={() => {
            handleCloseUploadModal();
            fetchFiles();
          }}
          // Rely only on the spread workaround for initialTag
          {...({ initialTag: targetTagForUpload } as Partial<PatientFilesUploadModalProps>)}
          worklistContext={targetTagForUpload ? `${targetTagForUpload.name} Case` : 'Cases'}
        />
      )}

      {viewAllTagGroup && (
        <Window title={`All Files for ${viewAllTagGroup.parentTagName}`} onClose={() => setViewAllTagGroup(null)}>
          <StyledCategoryFilesContainer>
            {viewAllTagGroup.files.length === 0 && <p>No files found for this tag group.</p>}
            {utilSortFilesByDate(viewAllTagGroup.files).map((file) => (
              <FileListItem key={file.id}>
                <div style={{ width: '60px', height: '60px', marginRight: '10px', flexShrink: 0 }}>
                  <FileThumbnailDisplay file={file} />
                </div>
                <div style={{ flex: 1, marginLeft: '10px', overflow: 'hidden' }}>
                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.fileName}</div>
                  <small>Uploaded: {file.created ? dayjs(file.created).format('MM/DD/YYYY HH:mm') : 'No date'}</small>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Tags: {file.tags?.map((t) => t.name).join(', ') || 'None'}</div>
                </div>
                <Button style={{ marginLeft: '10px' }} onClick={() => handleViewFileClickFromModal(file, viewAllTagGroup)}>
                  View
                </Button>
              </FileListItem>
            ))}
          </StyledCategoryFilesContainer>
        </Window>
      )}

      <UploadPipelineProvider trackerSource="Web-PatientFilesDashboard-TagView">
        <PageLayout>
          <MainContentArea>
            <UploadButtonContainer style={{ marginBottom: '20px' }}>
              <Button look={ButtonLooks.STANDARD} variant={ButtonVariants.PRIMARY} onClick={handleGlobalUpload}>
                <Icon icon={faFileLines} style={{ marginRight: '8px' }} /> Upload Files
              </Button>
            </UploadButtonContainer>

            <FilesGrid>
              {tagGroups.map((group) => (
                <TagGroupCard
                  key={group.parentTagId}
                  tagGroup={group}
                  onCardClick={handleCardClick}
                  onViewAll={handleViewAll}
                  onUploadClick={handleUploadForTagGroup}
                />
              ))}
              {tagGroups.length === 0 && !isLoading && <div>No target tag groups defined or files found matching them.</div>}
            </FilesGrid>
          </MainContentArea>

          <Sidebar>
            <Dashboard tagGroups={tagGroups} onUpload={handleGlobalUpload} />
            <TagGroupProgressDisplay tagGroups={tagGroups} />
          </Sidebar>
        </PageLayout>
      </UploadPipelineProvider>
    </>
  );
};

export const PatientFilesDashboard = React.memo(PatientFilesDashboardComponent);
